import {useEffect, useReducer, useState} from "react"
import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import Container from "@mui/material/Container"
import FormControlLabel from "@mui/material/FormControlLabel"
import Grid from "@mui/material/Grid"
import List from "@mui/material/List"
import Switch from "@mui/material/Switch"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import {init, reducer} from "../utils/reducer.js"
import {defaultFeeds} from "../utils/defaultFeeds.js"
import Feed from "./Feed"

const EditGroup = ({
  user,
  code,
  groups,
  currentGroup,
  showGroupList,
  appFeeds,
}) => {
  const [group, setGroup] = useState(() =>
    groups.all.find(g => g.key === currentGroup),
  )
  const [groupName, setGroupName] = useState(() => {
    const g = groups.all.find(gr => gr.key === currentGroup)
    return g?.name || ""
  })
  const [showCount, setShowCount] = useState(() => {
    const g = groups.all.find(gr => gr.key === currentGroup)
    return g?.showCount !== false
  })
  const [selected, setSelected] = useState([])
  const [message, setMessage] = useState("")
  const [disabledButton, setDisabledButton] = useState(false)
  const [feeds, updateFeed] = useReducer(reducer(), init)

  useEffect(() => {
    if (!groups) return

    setGroup(groups.all.find(g => g.key === currentGroup))
  }, [groups, currentGroup])

  useEffect(() => {
    for (const feed of defaultFeeds) {
      updateFeed(feed)
    }
  }, [])

  // Populate from already-loaded app feeds. App.jsx stores the full metadata
  // (title, image, description, language, defaultGroup) so no separate Holster
  // listener is needed here.
  useEffect(() => {
    for (const [url, feed] of Object.entries(appFeeds || {})) {
      if (!feed || !feed.title) continue
      updateFeed({
        key: url,
        title: feed.title,
        image: feed.image || "",
        description: feed.description || "",
        html_url: feed.url || "",
        language: feed.language || "",
        defaultGroup: feed.defaultGroup || "",
      })
    }
  }, [appFeeds])

  const selectFeed = f => {
    if (selected.includes(f.key)) {
      setSelected(selected.filter(key => key !== f.key))
    } else {
      setSelected([...selected, f.key])
    }
  }

  const updateGroup = async () => {
    if (!group) {
      setMessage("Group not set")
      return
    }
    if (!groupName) {
      setMessage("Group name required")
      return
    }

    const allFeeds = [...(group.feeds || []), ...selected]
    if (!group.bookmarks && allFeeds.length === 0) {
      const check = "Removing all feeds will remove the group. Continue?"
      if (!window.confirm(check)) return
    }

    setDisabledButton(true)
    setMessage("Updating group...")

    let data
    if (group.bookmarks) {
      data = {
        name: groupName,
        showCount,
        bookmarks: true,
        count: group.count || 0,
        latest: group.latest || 0,
        timestamp: group.timestamp || 0,
        text: group.text || "",
        author: group.author || "",
      }
    } else {
      // Convert feeds to an object to store in Holster.
      // See Display useEffect which converts back to an array.
      let f = {}
      for (let i = 0; i < allFeeds.length; i++) {
        const url = allFeeds[i]
        const feed = feeds.all.find(current => current.key === url)
        if (!feed) continue

        f[url] = true
        // Store each feed for the user to listen for updates in App.js.
        const err = await new Promise(res => {
          user.get("public").next("feeds").next(url).put(feed, res)
        })
        if (err) console.error(err)
      }
      data = {
        name: groupName,
        showCount,
        feeds: f,
        count: 0,
        latest: 0,
        text: "",
        author: "",
        timestamp: Date.now(),
      }
    }

    user
      .get("public")
      .next("groups")
      .next(group.key)
      .put(data, err => {
        if (err) {
          setDisabledButton(false)
          setMessage("Could not update group")
          console.error(err)
        } else {
          showGroupList(true)
        }
      })
  }

  return (
    <Container maxWidth="md">
      <Grid container>
        <Grid item xs={12}>
          <Card sx={{mt: 2}}>
            <CardContent>
              <Typography variant="h5">
                {group.bookmarks ? "Edit bookmark group" : "Edit group"}
              </Typography>
              <TextField
                id="editgroup-name"
                label="Group name"
                variant="outlined"
                fullWidth={true}
                margin="normal"
                value={groupName}
                onChange={event => setGroupName(event.target.value)}
              />
              {!group.bookmarks && (
                <FormControlLabel
                  sx={{display: "block", mt: 1}}
                  control={
                    <Switch
                      checked={showCount}
                      onChange={e => setShowCount(e.target.checked)}
                    />
                  }
                  label="Show unread count"
                />
              )}
              <Button
                sx={{mt: 1}}
                variant="contained"
                disabled={groupName === "" || disabledButton}
                onClick={updateGroup}
              >
                Submit
              </Button>
              {selected.length > 0 && !message && (
                <Typography sx={{m: 1}} variant="string">
                  {`Adding ${selected.length} feed${selected.length > 1 ? "s" : ""}`}
                </Typography>
              )}
              {message && (
                <Typography sx={{m: 1}} variant="string">
                  {message}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        {!group.bookmarks && (
          <>
            <Typography sx={{m: 1}}>Current feeds</Typography>
            <Grid item xs={12}>
              <List>
                {feeds &&
                  feeds.all.map(
                    f =>
                      f.title &&
                      (group.feeds || []).includes(f.key) && (
                        <Feed
                          key={f.key}
                          user={user}
                          code={code}
                          groups={groups}
                          currentGroup={currentGroup}
                          feed={f}
                          selected={false}
                          selectFeed={() => {}}
                        />
                      ),
                  )}
              </List>
              {(group.feeds || []).length > 0 &&
                feeds.all.filter(
                  f => f.title && (group.feeds || []).includes(f.key),
                ).length === 0 && (
                  <Typography sx={{m: 1}}>Loading...</Typography>
                )}
            </Grid>
            <Typography sx={{m: 1}}>Add feeds</Typography>
            <Grid item xs={12}>
              <List>
                {feeds &&
                  feeds.all.map(
                    f =>
                      f.title &&
                      !(group.feeds || []).includes(f.key) && (
                        <Feed
                          key={f.key}
                          user={user}
                          code={code}
                          groups={groups}
                          currentGroup={currentGroup}
                          feed={f}
                          selected={selected.includes(f.key)}
                          selectFeed={selectFeed}
                        />
                      ),
                  )}
              </List>
              {feeds &&
                feeds.all.filter(
                  f => f.title && !(group.feeds || []).includes(f.key),
                ).length === 0 && (
                  <Typography sx={{m: 1}}>
                    No more feeds available. Click add feed in the menu to add
                    more.
                  </Typography>
                )}
            </Grid>
          </>
        )}
      </Grid>
    </Container>
  )
}

export default EditGroup
